import { useState } from 'react'
import { ArrowDownUp, MoreHorizontal, Settings, Trash, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { UserWorkspace } from '@/api/types'
import { isWorkspaceAdmin } from '@/features/workspace/permissions'
import { MembersDialog } from '@/features/workspace/components/members-dialog'
import { TrashDialog } from '@/features/trash/components/trash-dialog'
import { TransferDialog } from '@/features/workspace/components/transfer-dialog'
import { WorkspaceSettingsDialog } from '@/features/workspace/components/workspace-settings-dialog'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'

/**
 * What can be done to the workspace on screen.
 *
 * Renaming, leaving and deleting are absent rather than hidden. The workspace is
 * the tenant's, named and populated by the identity provider, and the backend
 * refuses all three -- so what is left is reading the member list, reading the
 * trash, moving data in and out, and the admin-only settings.
 */
export function WorkspaceActions({ workspace }: { workspace: UserWorkspace }) {
  const { t } = useTranslation()
  const [dialog, setDialog] = useState<
    'members' | 'trash' | 'transfer' | 'settings' | null
  >(null)

  // Every one of the three panels behind this is admin-only at the endpoint:
  // API keys, the agent's MCP tokens, and what AI connections may not read. So
  // the entry is gated rather than the panels, and nothing inside re-checks it.
  const mayConfigure = isWorkspaceAdmin(workspace)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t('workspace.actions')}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setDialog('members')}>
            <Users />
            {t('member.title')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('trash')}>
            <Trash />
            {t('trash.open')}
          </DropdownMenuItem>
          {/*
            Offered to every member rather than to admins alone: the endpoints
            check what the caller may read, and somebody who can open the
            applications can already read what an export would contain.
          */}
          <DropdownMenuItem onSelect={() => setDialog('transfer')}>
            <ArrowDownUp />
            {t('transfer.menu')}
          </DropdownMenuItem>
          {mayConfigure && (
            <DropdownMenuItem onSelect={() => setDialog('settings')}>
              <Settings />
              {t('workspaceSettings.menu')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'members' && (
        <MembersDialog workspace={workspace} open onOpenChange={() => setDialog(null)} />
      )}

      {dialog === 'trash' && (
        <TrashDialog workspace={workspace} open onOpenChange={() => setDialog(null)} />
      )}

      {dialog === 'transfer' && (
        <TransferDialog workspace={workspace} open onOpenChange={() => setDialog(null)} />
      )}

      {dialog === 'settings' && (
        <WorkspaceSettingsDialog
          workspace={workspace}
          open
          onOpenChange={() => setDialog(null)}
        />
      )}
    </>
  )
}
